import type React from 'react'
import { Link } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui'
import { cn } from '@/lib/utils'

interface InsightCardProps {
  title: string
  description?: string
  icon?: string
  action?: { label: string; href: string }
  className?: string
  children: React.ReactNode
}

/** Card shell shared by the dashboard insight widgets */
export function InsightCard({
  title,
  description,
  icon,
  action,
  className,
  children,
}: InsightCardProps) {
  return (
    <Card className={cn('border-0 shadow-sm hover:shadow-md transition-shadow min-w-0', className)}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <CardTitle className="text-base font-semibold text-foreground">
            {icon && (
              <span className="mr-2" aria-hidden="true">
                {icon}
              </span>
            )}
            {title}
          </CardTitle>
          {action && (
            <Link
              to={action.href}
              className="shrink-0 text-xs font-medium text-primary hover:underline"
            >
              {action.label}
            </Link>
          )}
        </div>
        {description && (
          <CardDescription className="text-sm text-muted-foreground">{description}</CardDescription>
        )}
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  )
}

/** Muted one-liner for widgets without data */
export function InsightEmpty({ children }: { children: React.ReactNode }) {
  return <p className="py-4 text-center text-sm text-muted-foreground">{children}</p>
}

/** Small book cover with a neutral placeholder */
export function BookThumb({
  src,
  title,
  size = 'md',
}: {
  src?: string | null
  title: string
  size?: 'sm' | 'md'
}) {
  const box = size === 'sm' ? 'h-10 w-7 text-sm' : 'h-14 w-10 text-lg'
  return src ? (
    <img
      src={src}
      alt={title}
      loading="lazy"
      className={cn('shrink-0 rounded object-cover bg-muted', box)}
    />
  ) : (
    <div
      aria-hidden="true"
      className={cn('flex shrink-0 items-center justify-center rounded bg-muted', box)}
    >
      📖
    </div>
  )
}
